import { useRef } from "react"
import { FormikProps } from "formik"
import { useNavigate, useParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"

import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { COMPANY_ADMIN_ORGANIZATION_USERS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { getUserDetails } from "../../../../../users/userApi"

import { CompanyUserFormValues } from "../companyUserFormTypes"
import { buildSaveCompanyUserBody } from "../companyUserFormMapper"
import { CompanyAdminUsersQuery, createCompanyUser, updateCompanyUser } from "../usersApi"
import { CreateEditUserForm } from "../components/CreateEditUserForm"

export function CreateOrEditUser() {
    const navigate = useNavigate()
    const { userId: userIdParam } = useParams<{ userId?: string }>()

    const userId = userIdParam ? Number(userIdParam) : undefined
    const mode = userId ? "update" : "create"

    const formRef = useRef<FormikProps<CompanyUserFormValues>>(null)

    const detailsResult = useApiQuery(getUserDetails, {
        queryName: CompanyAdminUsersQuery.USER_DETAILS,
        dependencies: { userId: userId ?? 0 },
        enabled: Boolean(userId),
    })

    const existingUser = detailsResult.data?.user

    const { mutateAsync: callCreateUser, isLoading: isCreatingUser } = useApiMutation(createCompanyUser, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminUsersQuery.USERS_FOR_COMPANY] })
            navigate(COMPANY_ADMIN_ORGANIZATION_USERS_ROUTE)
        },
    })

    const { mutateAsync: callUpdateUser, isLoading: isUpdatingUser } = useApiMutation(updateCompanyUser, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminUsersQuery.USERS_FOR_COMPANY] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(CompanyAdminUsersQuery.USER_DETAILS, { userId: params.userId }),
            })
            navigate(COMPANY_ADMIN_ORGANIZATION_USERS_ROUTE)
        },
    })

    async function handleSubmit(values: CompanyUserFormValues) {
        const body = buildSaveCompanyUserBody(values, mode)

        if (mode === "update" && userId !== undefined) {
            await callUpdateUser({ userId, user: body.user, actions: body.actions })
        } else {
            await callCreateUser({ user: body.user, actions: body.actions })
        }
    }

    const isLoadingExistingUser = mode === "update" && detailsResult.isInitialLoading
    const isSubmitting = isCreatingUser || isUpdatingUser

    return (
        <>
            <LocalizedStrict
                id={mode === "create" ? "company-user-create-page-title" : "company-user-edit-page-title"}
                attrs={{ title: true }}
            >
                <PageHeader
                    page={Page.COMPANY_USERS_CREATE_USER}
                    title={mode === "create" ? "Create user" : "Edit user"}
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    rightElement={
                        <LocalizedStrict
                            id={mode === "create" ? "company-user-create-submit" : "company-user-edit-submit"}
                        >
                            <StandardLoadingButton
                                type="submit"
                                loading={isSubmitting}
                                onClick={() => formRef.current?.submitForm()}
                            >
                                {mode === "create" ? "Create" : "Save"}
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-organization">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-users">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_USERS_ROUTE}>Users</BreadcrumbLink>
                        </LocalizedStrict>
                        {mode === "create" ? (
                            <LocalizedStrict id="company-user-create-breadcrumb">
                                <Breadcrumb>Create user</Breadcrumb>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="company-user-edit-breadcrumb">
                                <Breadcrumb>Edit user</Breadcrumb>
                            </LocalizedStrict>
                        )}
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            {isLoadingExistingUser ? (
                <LoadingSpinner />
            ) : (
                <CreateEditUserForm ref={formRef} mode={mode} existingUser={existingUser} onSubmit={handleSubmit} />
            )}
        </>
    )
}
